/**
 * src/api.js — Centralized Axios instance for affixly-flow frontend.
 *
 * Attaches the Supabase JWT as Authorization: Bearer <token> on every
 * request so Flask can identify the authenticated user.
 *
 * Base URL resolution:
 *   - Staging (REACT_APP_API_BASE_URL set):
 *       baseURL = https://affixly-production.up.railway.app/api
 *   - Local dev (REACT_APP_API_BASE_URL unset):
 *       baseURL = /api → CRA proxy → http://localhost:5001/api
 */

import axios from 'axios'
import { getSupabase } from './lib/supabase'

const base    = process.env.REACT_APP_API_BASE_URL
const baseURL = base ? `${base}/api` : '/api'

const api = axios.create({ baseURL })

// Attach JWT from active Supabase session before every request
api.interceptors.request.use(async (config) => {
  const client = getSupabase()
  if (client) {
    const { data: { session } } = await client.auth.getSession()
    if (session?.access_token) {
      config.headers['Authorization'] = `Bearer ${session.access_token}`
    }
  }
  return config
})

// GP-17 — On 401, try a silent token refresh and transparently retry the
// original request before giving up. Only if refresh fails do we warn the user
// (with a countdown so they can copy unsaved work) and then sign out.

// Dedupe concurrent refreshes: the first 401 kicks off one refresh; other
// in-flight 401s await the same promise.
let refreshPromise = null
let expiryModalShown = false

function refreshSessionOnce() {
  if (!refreshPromise) {
    const client = getSupabase()
    refreshPromise = (client
      ? client.auth.refreshSession()
      : Promise.resolve({ data: { session: null }, error: new Error('no client') })
    )
      .then(({ data, error }) => (error ? null : data?.session || null))
      .catch(() => null)
      .finally(() => { setTimeout(() => { refreshPromise = null }, 0) })
  }
  return refreshPromise
}

// Lightweight, framework-free expiry warning so unsaved work can be saved
// before the redirect. Built with the DOM to avoid coupling api.js to React.
function showSessionExpiredModal() {
  if (expiryModalShown || typeof document === 'undefined') return
  expiryModalShown = true

  const finish = () => {
    const client = getSupabase()
    const go = () => { window.location.href = '/' }
    if (client) client.auth.signOut().then(go, go); else go()
  }

  const overlay = document.createElement('div')
  overlay.setAttribute('role', 'alertdialog')
  overlay.style.cssText =
    'position:fixed;inset:0;background:rgba(0,0,0,.55);display:flex;align-items:center;' +
    'justify-content:center;z-index:99999;font-family:inherit'
  overlay.innerHTML =
    '<div style="background:#1b1b22;color:#fff;border:1px solid rgba(255,255,255,.12);' +
    'border-radius:12px;padding:26px 28px;max-width:420px;box-shadow:0 8px 40px rgba(0,0,0,.6)">' +
    '<div style="font-size:16px;font-weight:600;margin-bottom:8px">Your session expired</div>' +
    '<div style="font-size:13px;line-height:1.6;opacity:.8;margin-bottom:18px">' +
    'Copy or save any unsaved work now. We\'ll take you back to sign in ' +
    '<span id="flow-expiry-count">30</span>s.</div>' +
    '<div style="display:flex;gap:10px;justify-content:flex-end">' +
    '<button id="flow-expiry-stay" style="background:transparent;color:#c7f36b;border:1px solid ' +
    'rgba(199,243,107,.4);border-radius:8px;padding:8px 14px;font-size:13px;cursor:pointer">Stay a moment</button>' +
    '<button id="flow-expiry-now" style="background:#c7f36b;color:#111;border:none;border-radius:8px;' +
    'padding:8px 14px;font-size:13px;font-weight:600;cursor:pointer">Sign in again</button>' +
    '</div></div>'
  document.body.appendChild(overlay)

  let remaining = 30
  const countEl = overlay.querySelector('#flow-expiry-count')
  const timer = setInterval(() => {
    remaining -= 1
    if (countEl) countEl.textContent = String(remaining)
    if (remaining <= 0) { clearInterval(timer); finish() }
  }, 1000)

  overlay.querySelector('#flow-expiry-now').onclick = () => { clearInterval(timer); finish() }
  // "Stay a moment" buys the user extra time to copy content; it does not keep
  // the dead session alive — it just pauses the auto-redirect.
  overlay.querySelector('#flow-expiry-stay').onclick = () => {
    clearInterval(timer)
    const note = overlay.querySelector('#flow-expiry-count')
    if (note && note.parentElement) note.parentElement.textContent =
      'Take your time — click “Sign in again” when you\'re ready.'
  }
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config
    if (error.response?.status === 401 && original && !original._retry) {
      original._retry = true
      const session = await refreshSessionOnce()
      if (session?.access_token) {
        // Refresh succeeded — replay the original request transparently.
        original.headers = original.headers || {}
        original.headers['Authorization'] = `Bearer ${session.access_token}`
        return api(original)
      }
      // Refresh failed — warn before signing out so work can be saved.
      showSessionExpiredModal()
    }
    return Promise.reject(error)
  }
)

export default api
